import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import useRegistryEntryHistory from '../hooks/useRegistryEntryHistory';
import { getStatusConfig } from '../config/statusConfig';
import { formatDate } from '../utils/dateUtils';
import { changeLines } from '../utils/registryTransfer';
import { isSyncable } from '../utils/registrySourceConfig';
import { MONO_FONT } from '../constants/typography';
import { CONTENITORE_TABELLA, TESTATA_TABELLA } from './common/stiliTabella';

const PAGE_SIZE = 20;
const ORIGIN_LABELS = { MANUAL: 'manuale', IMPORT: 'import' };
const COLUMNS = 6;
const MONO_CELL = { fontFamily: MONO_FONT, fontWeight: 500 };
// «voce eliminata» e' testo, non un valore: resta nel font del tema
const NOTE_LINE = 'voce eliminata';

const ChangeChip = ({ changeType }) => {
  const { label, color } = getStatusConfig('REGISTRY_ENTRY_CHANGE', changeType);
  return <Chip size="small" variant="filled" color={color} label={label} />;
};

/** Una riga per campo; i valori in monospazio, come le chiavi altrove. */
const ChangesCell = ({ row, schema }) => {
  const lines = changeLines(row, schema);
  if (lines.length === 0) {
    return <Typography variant="body2" color="text.secondary">nessuna differenza</Typography>;
  }
  // ogni riga comincia con l'etichetta del campo (o e' «voce eliminata»): il testo e' unico
  return lines.map((line) => (
    <Typography
      key={line}
      variant="body2"
      sx={line === NOTE_LINE ? undefined : { fontFamily: MONO_FONT, fontSize: '0.8125rem' }}
    >
      {line}
    </Typography>
  ));
};

const StatusBlock = ({ children }) => (
  <Box sx={{ py: 3, textAlign: 'center' }}>{children}</Box>
);

const HistoryContent = ({ registry, entry }) => {
  const { rows, totalElements, page, setPage, loading, error } = useRegistryEntryHistory(
    registry,
    entry?.id,
    { size: PAGE_SIZE },
  );

  if (error) {
    return <StatusBlock><Typography variant="body2" color="error.main">{error}</Typography></StatusBlock>;
  }
  if (loading && rows.length === 0) {
    return <StatusBlock><CircularProgress size={24} aria-label="Caricamento storico" /></StatusBlock>;
  }
  if (!loading && rows.length === 0 && totalElements === 0) {
    return (
      <StatusBlock>
        <Typography variant="body2" color="text.secondary">
          {isSyncable(registry)
            ? 'Nessuna modifica registrata. Le voci sincronizzate da una sorgente esterna non producono storico.'
            : 'Nessuna modifica registrata.'}
        </Typography>
      </StatusBlock>
    );
  }

  return (
    <TableContainer {...CONTENITORE_TABELLA}>
      <Table size="small" aria-label="Storico modifiche" aria-busy={loading}>
        <TableHead>
          <TableRow sx={TESTATA_TABELLA}>
            <TableCell>Quando</TableCell>
            <TableCell>Chi</TableCell>
            <TableCell>Origine</TableCell>
            <TableCell>Operazione</TableCell>
            <TableCell>Voce</TableCell>
            <TableCell>Modifiche</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* pagina oltre l'ultima (le righe sono diminuite): si resta nella tabella per tornare indietro */}
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMNS} align="center" sx={{ py: 3 }}>
                <Typography variant="body2" color="text.secondary">Nessuna modifica registrata.</Typography>
              </TableCell>
            </TableRow>
          ) : rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>{formatDate(row.changedAt)}</TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>{row.changedBy}</TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>{ORIGIN_LABELS[row.origin] || row.origin}</TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}><ChangeChip changeType={row.changeType} /></TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>
                {row.entryKey
                  ? <Chip size="small" variant="outlined" label={row.entryKey} sx={MONO_CELL} />
                  : '—'}
              </TableCell>
              <TableCell><ChangesCell row={row} schema={registry.schema} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalElements}
        page={page}
        onPageChange={(_e, next) => setPage(next)}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
        labelRowsPerPage="Righe per pagina"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} di ${count}`}
      />
    </TableContainer>
  );
};

/** Storico delle modifiche delle voci di un'anagrafica interna, o di una sola voce se `entry` e' passata. */
const RegistryEntryHistoryDialog = ({ open, registry, entry, onClose }) => (
  <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <Box component="span">Storico modifiche · {registry?.name || registry?.code}</Box>
      {entry?.entryKey && (
        <Chip size="small" variant="outlined" label={entry.entryKey} sx={MONO_CELL} />
      )}
    </DialogTitle>
    <DialogContent>
      {open && registry && <HistoryContent registry={registry} entry={entry} />}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Chiudi</Button>
    </DialogActions>
  </Dialog>
);

export default RegistryEntryHistoryDialog;
